/**
 * Live Monitoring – 3 tabs: Zone Management, Manage Use Case, Configure Use Case.
 */

import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import SvgIcon from "@mui/material/SvgIcon";
import {
  Dashboard as DashboardIcon,
  Map as ZoneManagementIcon,
  Build as ConfigureUseCaseIcon,
  Delete as RecycleBinIcon,
} from "@mui/icons-material";
import AIDetectionDashboard from "../../components/aiDetectionSections/AIDetectionDashboard.tsx";
import { ZoneManagementTab } from "../../components/aiDetectionSections/ZoneManagementTab.tsx";
import { ManageUseCaseTab } from "../../components/aiDetectionSections/ManageUseCaseTab.tsx";
import { ConfigureUseCaseTab } from "../../components/aiDetectionSections/ConfigureUseCaseTab.tsx";
import { RecycleBinTab } from "../../components/aiDetectionSections/RecycleBinTab.tsx";
import { useRecycleBinStore } from "../../store/recycleBinStore.ts";
import { fetchAndApplyRecycleBinTotals } from "../../utils/recycleBinTotalsSync.ts";

/** Custom 3D box/cube icon for Manage Use Case tab */
function ManageUseCaseIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M21.3353 12.0795L17.25 10.0357V5.25C17.2499 5.11075 17.2111 4.97428 17.1378 4.85586C17.0646 4.73743 16.9598 4.64174 16.8353 4.5795L12.3353 2.3295C12.2312 2.27748 12.1164 2.2504 12 2.2504C11.8836 2.2504 11.7689 2.27748 11.6648 2.3295L7.16475 4.5795C7.04019 4.64174 6.93542 4.73743 6.86216 4.85586C6.78891 4.97428 6.75007 5.11075 6.75 5.25V10.0365L2.66475 12.0787C2.54008 12.141 2.43523 12.2369 2.36197 12.3554C2.28871 12.474 2.24994 12.6106 2.25 12.75V18C2.24994 18.1394 2.28871 18.276 2.36197 18.3946C2.43523 18.5131 2.54008 18.609 2.66475 18.6713L7.16475 20.9213C7.26885 20.9733 7.38363 21.0003 7.5 21.0003C7.61637 21.0003 7.73115 20.9733 7.83525 20.9213L12 18.8385L16.1647 20.9213C16.2688 20.9733 16.3836 21.0003 16.5 21.0003C16.6164 21.0003 16.7312 20.9733 16.8353 20.9213L21.3353 18.6713C21.4599 18.609 21.5648 18.5131 21.638 18.3946C21.7113 18.276 21.7501 18.1394 21.75 18V12.75C21.7501 12.6106 21.7113 12.474 21.638 12.3554C21.5648 12.2369 21.4599 12.141 21.3353 12.0787M15.75 10.0357L12.75 11.5357V7.96275L15.75 6.46275V10.0357ZM12 3.83775L14.823 5.25L12 6.6615L9.177 5.25L12 3.83775ZM8.25 6.46275L11.25 7.96275V11.5357L8.25 10.0357V6.46275ZM6.75 19.0365L3.75 17.5365V13.9635L6.75 15.4635V19.0365ZM7.5 14.1615L4.677 12.75L7.5 11.3385L10.323 12.75L7.5 14.1615ZM8.25 15.4635L11.25 13.9635V17.5365L8.25 19.0365V15.4635ZM15.75 19.0365L12.75 17.5365V13.9635L15.75 15.4635V19.0365ZM16.5 14.1615L13.677 12.75L16.5 11.3385L19.323 12.75L16.5 14.1615ZM20.25 17.5365L17.25 19.0365V15.4635L20.25 13.9635V17.5365Z"
      />
    </SvgIcon>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const active = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      id={`ai-tabpanel-3only-${index}`}
      aria-labelledby={`ai-tab-3only-${index}`}
      style={{
        flex: 1,
        minHeight: 0,
        display: active ? "flex" : "none",
        flexDirection: "column",
        width: "100%",
      }}
      {...other}
    >
      {active && (
        <Box
          sx={{
            pt: "6px",
            pr: "10px",
            pl: "10px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

export function AIDetectionTabsThreeOnly() {
  const [activeTab, setActiveTab] = useState(0);
  const [isZoneConfigActive, setIsZoneConfigActive] = useState(false);
  const recycleBinTableTotals = useRecycleBinStore(
    (state) => state.recycleBinTableTotals,
  );
  const recycleBinCount = Object.values(recycleBinTableTotals).reduce(
    (sum, n) => sum + n,
    0,
  );

  useEffect(() => {
    void fetchAndApplyRecycleBinTotals();
  }, []);

  useEffect(() => {
    const onRecycleChanged = () => {
      void fetchAndApplyRecycleBinTotals();
    };
    window.addEventListener("configure-use-case-recycle-changed", onRecycleChanged);
    return () =>
      window.removeEventListener(
        "configure-use-case-recycle-changed",
        onRecycleChanged,
      );
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (!isZoneConfigActive) {
      setActiveTab(newValue);
    }
  };

  const setZoneConfigActive = (active: boolean) => {
    setIsZoneConfigActive(active);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Live Monitoring tabs (Dashboard, Zone Management, Manage Use Case, Configure Use Case, Recycle Bin)"
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: "#2932E5", height: 3 },
          }}
        >
          <Tab
            icon={<DashboardIcon />}
            iconPosition="start"
            label="Dashboard"
            id="ai-tab-3only-0"
            aria-controls="ai-tabpanel-3only-0"
            sx={tabSx}
          />
          <Tab
            icon={<ZoneManagementIcon />}
            iconPosition="start"
            label="Zone Management"
            id="ai-tab-3only-1"
            aria-controls="ai-tabpanel-3only-1"
            sx={tabSx}
          />
          <Tab
            icon={<ManageUseCaseIcon />}
            iconPosition="start"
            label="Manage Use Cases"
            id="ai-tab-3only-2"
            aria-controls="ai-tabpanel-3only-2"
            sx={tabSx}
          />
          <Tab
            icon={<ConfigureUseCaseIcon />}
            iconPosition="start"
            label="Configure Use Case"
            id="ai-tab-3only-3"
            aria-controls="ai-tabpanel-3only-3"
            sx={tabSx}
          />
          <Tab
            icon={
              recycleBinCount > 0 ? (
                <Badge
                  badgeContent={recycleBinCount}
                  color="primary"
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  overlap="rectangular"
                  sx={{
                    '& .MuiBadge-badge': {
                      top: 2,
                      right: 2,
                    },
                  }}
                >
                  <RecycleBinIcon sx={{ display: 'block' }} />
                </Badge>
              ) : (
                <RecycleBinIcon />
              )
            }
            iconPosition="start"
            label="Recycle Bin"
            id="ai-tab-3only-4"
            aria-controls="ai-tabpanel-3only-4"
            sx={tabSx}
          />
        </Tabs>
      </Box>

      {/* <TabPanel value={activeTab} index={0}>
        <AIDetectionDashboard />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <ZoneManagementTab onZoneConfigChange={setZoneConfigActive} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <ManageUseCaseTab isActive={activeTab === 2} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ConfigureUseCaseTab />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <RecycleBinTab />
      </TabPanel> */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <TabPanel value={activeTab} index={0}>
        <AIDetectionDashboard />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <ZoneManagementTab onZoneConfigChange={setZoneConfigActive} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <ManageUseCaseTab isActive={activeTab === 2} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ConfigureUseCaseTab />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <RecycleBinTab />
      </TabPanel>
      </Box>
      {/* <TabPanel value={activeTab} index={2}>
        <ManageUseCaseTab isActive={activeTab === 2} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <ConfigureUseCaseTab />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <RecycleBinTab />
      </TabPanel> */}
    </Box>
  );
}

const tabSx = {
  textTransform: "none" as const,
  fontFamily: "Roboto, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  minHeight: 48,
  color: "text.secondary",
  "&.Mui-selected": { color: "#2932E5" },
  "& .MuiTab-iconWrapper": { mr: 1 },
};
